import React, { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import StoreFunctions from '../../../stores/storeFunctions'
import { Diamond, SourceType, CurrencyInfo, DiamondsTableProps } from 'types/interfaces'
import { useAppContext } from 'lib/context/AppContext'

type CutGrade = 'I' | 'EX' | 'VG' | 'G' | 'F'
type ClarityGrade = 'SI2' | 'SI1' | 'VVS1' | 'VS1' | 'VS2' | 'S1' | 'S2' | 'I1' | 'I2' | 'I3'

const clarityRank: Record<ClarityGrade, number> = {
  SI2: 0,
  SI1: 1,
  VVS1: 2,
  VS1: 3,
  VS2: 4,
  S1: 5,
  S2: 6,
  I1: 7,
  I2: 8,
  I3: 9
}

const storeFunctions = new StoreFunctions()

const groupBy = <T, K>(list: T[], keyGetter: (item: T) => K): Map<K, T[]> => {
  const map = new Map<K, T[]>()
  list.forEach(item => {
    const key = keyGetter(item)
    const collection = map.get(key)
    if (!collection) {
      map.set(key, [item])
    } else {
      collection.push(item)
    }
  })
  return map
}

const compareDiamonds = (a: Diamond, b: Diamond): number => {
  if (a.weight < b.weight) return -1
  if (a.weight > b.weight) return 1

  if (a.color < b.color) return -1
  if (a.color > b.color) return 1

  if (clarityRank[a.clarity as ClarityGrade] > clarityRank[b.clarity as ClarityGrade]) {
    return -1
  }
  if (clarityRank[a.clarity as ClarityGrade] < clarityRank[b.clarity as ClarityGrade]) {
    return 1
  }

  return 0
}

const PriceLabel: FC<{ d: Diamond; currencyInfo: CurrencyInfo }> = ({ d, currencyInfo }) => {
  return (
    <label
      dangerouslySetInnerHTML={{
        __html: storeFunctions.formatPrice(d.retail_price, currencyInfo)
      }}
    ></label>
  )
}
const TypeLabel: FC<{ d: Diamond; sourceTypes: SourceType[] }> = ({ d, sourceTypes }) => {
  const type = sourceTypes.find(sourceType => sourceType.name === d.type)
  const label = type ? type.label : d.type
  return <label htmlFor={d.sku}>{label}</label>
}
const CaratLabel: FC<{ d: Diamond }> = ({ d }) => (
  <label htmlFor={d.sku}>{d.weight.toFixed(2)}</label>
)
const ColorLabel: FC<{ d: Diamond }> = ({ d }) => <label htmlFor={d.sku}>{d.color}</label>
const ClarityLabel: FC<{ d: Diamond }> = ({ d }) => <label htmlFor={d.sku}>{d.clarity}</label>
const GradeLabel: FC<{ d: Diamond }> = ({ d }) => {
  const { t } = useTranslation()
  const grades: Record<CutGrade, string> = {
    I: t('diamondsTable.gradeIdeal'),
    EX: t('diamondsTable.gradeExcellent'),
    VG: t('diamondsTable.gradeVeryGood'),
    G: t('diamondsTable.gradeGood'),
    F: t('diamondsTable.gradeFair')
  }
  const grade =
    d.shape === 'round' ? d.cut_grade_description || grades[d.cut_grade] || d.cut_grade : '-'
  return <label htmlFor={d.sku}>{grade}</label>
}

const DiamondRow: FC<{
  d: Diamond
  sourceTypes: SourceType[]
  currencyInfo: CurrencyInfo
  stoneType: string
}> = ({ d, sourceTypes, currencyInfo, stoneType }) => {
  const icon = storeFunctions.translateShapeAPINameToIcon(d) || ''
  return (
    <div className="unbridaled-diamond-row">
      <div
        className="unbridaled-diamond-column unbridaled-diamond-img-holder"
        dangerouslySetInnerHTML={{ __html: icon }}
      ></div>
      <div className="unbridaled-diamond-column unbridaled-type">
        <TypeLabel d={d} sourceTypes={sourceTypes} />
      </div>
      <div className="unbridaled-diamond-column unbridaled-carat">
        <CaratLabel d={d} />
      </div>
      <div className="unbridaled-diamond-column unbridaled-color">
        <ColorLabel d={d} />
      </div>
      {stoneType != "gemstone" ? (
        <div className="unbridaled-diamond-column unbridaled-clarity">
          <ClarityLabel d={d} />
        </div>
      ) : null}
      <div className="unbridaled-diamond-column unbridaled-cut">
        <GradeLabel d={d} />
      </div>
      <div className="unbridaled-diamond-column unbridaled-price">
        <PriceLabel d={d} currencyInfo={currencyInfo} />
      </div>
    </div>
  )
}

const RowHeader: FC<{
  stoneType: string
}> = ({stoneType}) => {
  const { t } = useTranslation()
  return (
    <div className="unbridaled-header-table">
      <div className="unbridaled-header-column unbridaled-header-image"></div>
      <div className="unbridaled-header-column unbridaled-header-origin">{t('diamondsTable.origin')}</div>
      <div className="unbridaled-header-column unbridaled-header-carat">{t('diamondsTable.carat')}</div>
      <div className="unbridaled-header-column unbridaled-header-color">{t('diamondsTable.color')}</div>
      {stoneType != "gemstone" ? (<div className="unbridaled-header-column unbridaled-header-clarity">{t('diamondsTable.clarity')}</div>) : null}
      <div className="unbridaled-header-column unbridaled-header-cut">{t('diamondsTable.cut')}</div>
      <div className="unbridaled-header-column unbridaled-header-price">{t('diamondsTable.price')}</div>
    </div>
  )
}

export const DiamondsTable: FC<DiamondsTableProps> = ({
  selectedDiamonds,
  sourceTypes,
  stoneType = "diamond"
}) => {
  const groupedByShape = useMemo(() => groupBy(selectedDiamonds, d => d.shape), [selectedDiamonds])
  const { currencyInfo } = useAppContext()

  return (
    <>
      <RowHeader stoneType={stoneType} />
      {[...groupedByShape.keys()].map((shape, i) => {
        const diamonds = groupedByShape.get(shape) || []
        diamonds.sort(compareDiamonds)

        return (
          <div key={i} id={`diamonds-${shape}`} className="unbridaled-diamonds-table">
            <div className="unbridaled-rows-shape">{shape}</div>
            {diamonds.map(d => (
              <DiamondRow key={d.sku} d={d} sourceTypes={sourceTypes} currencyInfo={currencyInfo} stoneType={stoneType}/>
            ))}
          </div>
        )
      })}
    </>
  )
}

export default DiamondsTable
